import React from 'react';
import { Calendar, Car, Eye, Mail, Phone, RefreshCw, Search } from 'lucide-react';
import { DataTable } from '../../common/DataTable';
import Card from '../../common/Card';
import EmptyState from '../../common/EmptyState';
import Button from '../../common/Button';
import CorvinArt from '../../brand/CorvinArt';

// Extracted verbatim from JobManagement's customers tab block (Task 7).
// Presentational only — the loaders come from useCustomers via the
// orchestrator; this tab calls no API itself.
const CustomersTab = ({
  stats,
  isLoading,
  customers,
  customerSearch,
  setCustomerSearch,
  customersPage,
  setCustomersPage,
  customersTotalPages,
  loadCustomers,
  loadCustomerJobs
}) => {
  const columns = [
    {
      header: 'Customer',
      accessor: 'name',
      render: (customer) => (
        <span className="font-medium text-ink">{customer.name}</span>
      ),
    },
    {
      header: 'Contact',
      accessor: 'phone',
      render: (customer) => (
        customer.phone || customer.email ? (
          <div className="flex flex-col leading-tight">
            {customer.phone && <span className="text-ink-soft">{customer.phone}</span>}
            {customer.email && <span className="text-small text-ink-muted">{customer.email}</span>}
          </div>
        ) : (
          <span className="text-ink-soft">—</span>
        )
      ),
    },
    {
      header: 'Vehicles',
      accessor: 'vehicles',
      render: (customer) => (
        <span className="text-ink-soft">{customer.vehicles || '—'}</span>
      ),
    },
    { header: 'Jobs', accessor: 'total_jobs', numeric: true },
    {
      header: 'Last Visit',
      accessor: 'last_job_date',
      render: (customer) => (
        <span className="font-mono tabular-nums text-ink-soft">
          {customer.last_job_date ? new Date(customer.last_job_date).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      sortable: false,
      render: (customer) => (
        <Button variant="ghost" size="sm" onClick={() => loadCustomerJobs(customer.name)}>
          <Eye size={16} strokeWidth={1.5} className="mr-1" />
          View Jobs
        </Button>
      ),
    },
  ];

  const renderCustomerCard = (customer) => (
    <Card className="p-4">
      <div className="font-medium text-ink">{customer.name}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-small text-ink-muted">
        {customer.phone && (
          <span className="inline-flex items-center gap-1">
            <Phone size={14} strokeWidth={1.5} />{customer.phone}
          </span>
        )}
        {customer.email && (
          <span className="inline-flex items-center gap-1">
            <Mail size={14} strokeWidth={1.5} />{customer.email}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-small text-ink-soft">
        <span className="inline-flex items-center gap-1">
          <Car size={14} strokeWidth={1.5} />{customer.vehicles || '—'}
        </span>
        <span className="font-mono tabular-nums">{customer.total_jobs} jobs</span>
        {customer.last_job_date && (
          <span className="inline-flex items-center gap-1 font-mono tabular-nums">
            <Calendar size={14} strokeWidth={1.5} />
            {new Date(customer.last_job_date).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="mt-3">
        <Button
          variant="secondary"
          size="sm"
          className="min-h-touch w-full"
          onClick={() => loadCustomerJobs(customer.name)}
        >
          <Eye size={16} strokeWidth={1.5} className="mr-1" />
          View Jobs
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Search and Controls */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-muted h-4 w-4" />
              <input
                type="text"
                placeholder="Search customers by name, phone, or email..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
          {/* Secondary: the one solid accent button on this screen is "New Job". */}
          <Button variant="secondary" icon={RefreshCw} loading={isLoading} onClick={loadCustomers}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-surface-card rounded-card shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border-default">
          <h3 className="text-lg font-medium text-ink">Customer Directory</h3>
        </div>

        <div className="p-6">
          {customers.length === 0 && !isLoading ? (
            <EmptyState
              illustration={<CorvinArt pose="standing" size={96} />}
              title="No customers yet"
              description="Customers show up here once they have a job. There's nothing to set up."
            />
          ) : (
            <DataTable
              data={customers}
              columns={columns}
              density="compact"
              loading={isLoading}
              pagination={false}
              filterable={false}
              sortable={false}
              renderCard={renderCustomerCard}
            />
          )}

          {/* Pagination */}
          {customersTotalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-ink">
                Page {customersPage} of {customersTotalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCustomersPage(Math.max(1, customersPage - 1))}
                  disabled={customersPage === 1}
                  className="px-3 py-1 border border-border-default rounded text-ink hover:bg-surface-sunken disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCustomersPage(Math.min(customersTotalPages, customersPage + 1))}
                  disabled={customersPage === customersTotalPages}
                  className="px-3 py-1 border border-border-default rounded text-ink hover:bg-surface-sunken disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomersTab;
